/**
 * Single source of truth for the languages the admin app ships translations for.
 *
 * Must stay in lockstep with `public/i18n/<code>.json`: adding a code here
 * without the matching file leaves the UI on raw translation keys.
 */
export const LANG_EN = 'en';
export const LANG_RO = 'ro';
export const LANG_ES = 'es';
export const LANG_IT = 'it';
export const LANG_DE = 'de';
export const LANG_FR = 'fr';

export const DEFAULT_LANG = LANG_EN;

export interface AppLanguage {
  /** ngx-translate code, matches the i18n filename. */
  readonly code: string;
  /** Name shown in its own language, never localized. */
  readonly nativeName: string;
  /** Two-letter badge for the compact switcher. */
  readonly badge: string;
  /** BCP-47 tag for Intl date/number/currency formatting. */
  readonly locale: string;
}

export const APP_LANGUAGES: readonly AppLanguage[] = [
  { code: LANG_EN, nativeName: 'English', badge: 'EN', locale: 'en-US' },
  { code: LANG_RO, nativeName: 'Română', badge: 'RO', locale: 'ro-RO' },
  { code: LANG_ES, nativeName: 'Español', badge: 'ES', locale: 'es-ES' },
  { code: LANG_IT, nativeName: 'Italiano', badge: 'IT', locale: 'it-IT' },
  { code: LANG_DE, nativeName: 'Deutsch', badge: 'DE', locale: 'de-DE' },
  { code: LANG_FR, nativeName: 'Français', badge: 'FR', locale: 'fr-FR' }
];

export const SUPPORTED_LANG_CODES: readonly string[] = APP_LANGUAGES.map(l => l.code);

export function isSupportedLang(code: string | null | undefined): boolean {
  return !!code && SUPPORTED_LANG_CODES.includes(code);
}

export function resolveLang(code: string | null | undefined): string {
  return isSupportedLang(code) ? (code as string) : DEFAULT_LANG;
}

/** BCP-47 tag for Intl APIs; falls back to the default language's locale. */
export function localeFor(code: string | null | undefined): string {
  return APP_LANGUAGES.find(l => l.code === code)?.locale
    ?? APP_LANGUAGES[0].locale;
}
